import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal }                                          from 'react-dom';
import { Button, Checkbox, Dropdown, Icon, Popup }               from 'semantic-ui-react';
import type { SemanticICONS }                                    from 'semantic-ui-react';
import { Flexbox, StyledContainer }                              from '@src/views/containers';
import { CorvalUIColor, FlexAlign, FlexJustify, Position, Size } from '@src/types';
import { useSystemModals, useSystemToasts, useCurrentUser }      from '@src/hooks';
import { useCentralCalls }                                       from '@src/rest/CentralCalls';
import { useResearchApi }                                        from '@src/rest/ResearchApiCalls';
import AIAgentManagement                                          from './AIAgentManagement';

export type AIWindowMode = 'collapsed' | 'open' | 'extended';

interface AIWindowProps {
    /** Client selected on the Consultant Dash; scopes the agent list and agent management. */
    clientId?: string;
    /** Consultant selected on the Consultant Dash, if any; scopes the agent list. */
    consultantId?: string;
    mode: AIWindowMode;
    toggleCollapsed: () => void;
    toggleExtended: () => void;
}

/** Row from tng.AIAgent (via TableSelect on the AIAgents DbSet). */
interface Agent {
    id: string;
    name: string;
    defaultAIModelId: string;
}

/** Row from tng.AIModel (via TableSelect on the AIModels DbSet). Ratings are null until set. */
export interface AIModel {
    id: string;
    name: string;
    provider: string;
    /** Deployment/model name sent to the research API to run the prompt (tng.AIModel.ModelIdentifier). */
    modelIdentifier: string;
    /** 1 (cheapest) - 3 (most expensive) */
    cost?: number | null;
    /** 1 (slowest) - 3 (fastest) */
    speed?: number | null;
    /** 1 (basic) - 3 (advanced) */
    reasoning?: number | null;
    description?: string | null;
}

interface Conversation {
    conversationId: string;
    name: string;
    /** ISO date string */
    date: string;
    isShared: boolean;
    /** tng.AIAgent the conversation was held with. */
    agentId: string;
    /** Only set for shared conversations, whose agent may not be in the current agent list. */
    agentName?: string;
    /** Only set for shared conversations. */
    ownerName?: string;
}

/** In-progress share selection for the selected conversation; only exists while share mode is on. */
interface ShareDraft {
    isShared: boolean;
    messageIds: Set<string>;
}

interface ConversationAttachment {
    attachmentType: string;
    attachmentId: string;
    attachmentName: string;
    /** Present only for agent-generated documents not yet saved to blob storage; enables download/drag-to-save. */
    contentBase64?: string;
    contentType?: string;
}

/** Custom drag MIME type so the drop zone only accepts documents dragged from inside the app. */
export const AI_CONTEXT_DOCUMENT_MIME = 'application/x-corval-document';

/** Payload carried by a document drag; the agent only needs `documentId` to fetch it server-side. */
export interface AIContextDocument {
    documentId: string;
    /** Display name of the owning DocumentItem (not the underlying file name). */
    name: string;
    /** Extension without a leading dot (e.g. "docx"), matching Document.Extension. */
    extension: string;
}

const formatContextDocumentTitle = ( contextDocument: AIContextDocument ): string =>
    contextDocument.extension ? `${ contextDocument.name } (.${ contextDocument.extension })` : contextDocument.name;

export const setAIContextDragData = ( dataTransfer: DataTransfer, contextDocument: AIContextDocument ) => {
    dataTransfer.setData( AI_CONTEXT_DOCUMENT_MIME, JSON.stringify( contextDocument ) );
    dataTransfer.setData( 'text/plain', formatContextDocumentTitle( contextDocument ) );
    dataTransfer.effectAllowed = 'copy';
};

const isAIContextDrag = ( dataTransfer: DataTransfer ): boolean =>
    Array.from( dataTransfer.types ).includes( AI_CONTEXT_DOCUMENT_MIME );

const parseAIContextDrag = ( dataTransfer: DataTransfer ): AIContextDocument | null => {
    try {
        const parsed = JSON.parse( dataTransfer.getData( AI_CONTEXT_DOCUMENT_MIME ) );
        return parsed?.documentId ? parsed as AIContextDocument : null;
    } catch {
        return null;
    }
};

interface ConversationMessage {
    type: 'prompt' | 'response';
    content: string;
    /** ISO date-time string */
    date: string;
    /** consultantId when type is 'prompt', agentId when type is 'response' */
    userId: string;
    attachments: ConversationAttachment[];
    /** dbo.AIConversationMessage id. A saved row holds both the prompt and its response, so the pair shares one id. */
    messageId?: string;
    isShared?: boolean;
}

/** Key used to hold optimistic messages for a conversation that hasn't been created in the database yet. */
const NEW_CONVERSATION_KEY = '__new__';

const ATTACHMENT_ICONS: Record<string, SemanticICONS> = {
    document: 'file alternate outline' as SemanticICONS,
    spreadsheet: 'file excel outline' as SemanticICONS,
    image: 'file image outline' as SemanticICONS,
    link: 'linkify' as SemanticICONS,
};

const RATING_SCALE = [ 1, 2, 3 ];

const ScaleIndicator: FC<{ value?: number | null; label: string; icon: SemanticICONS; className: string }> = ( { value, label, icon, className } ) => (
    <span className={ className } title={ value ? `${ label }: ${ value }/3` : `${ label }: not rated` }>
        { RATING_SCALE.map( step => (
            <Icon
                key={ step }
                name={ icon }
                className={ value && step <= value ? 'ai-scale-icon filled' : 'ai-scale-icon empty' }
            />
        ) ) }
    </span>
);

export const CostIndicator: FC<{ cost?: number | null }> = ( { cost } ) => (
    <ScaleIndicator value={ cost } label={ 'Cost' } icon={ 'dollar' as SemanticICONS } className={ 'ai-model-cost' }/>
);

export const SpeedIndicator: FC<{ speed?: number | null }> = ( { speed } ) => (
    <ScaleIndicator value={ speed } label={ 'Speed' } icon={ 'bolt' as SemanticICONS } className={ 'ai-model-speed' }/>
);

export const ReasoningIndicator: FC<{ reasoning?: number | null }> = ( { reasoning } ) => (
    <ScaleIndicator value={ reasoning } label={ 'Reasoning' } icon={ 'lightbulb' as SemanticICONS } className={ 'ai-model-reasoning' }/>
);

const MODEL_TOOLTIP_GAP   = 8;
const MODEL_TOOLTIP_WIDTH = 280;

/**
 * Hover tooltip for a model's description. Rendered into document.body with fixed positioning so the
 * clipped content pane can't cut it off, and pointer-events: none so it never steals the item's hover.
 */
const ModelDescriptionTooltip: FC<{ model: AIModel; anchor: DOMRect }> = ( { model, anchor } ) => {
    // The AI panel sits on the right of the screen, so prefer the left side; fall back to the right if there's no room.
    const placeLeft = anchor.left - MODEL_TOOLTIP_GAP - MODEL_TOOLTIP_WIDTH >= 0;

    return createPortal(
        <div
            role={ 'tooltip' }
            className={ 'ai-model-description-tooltip' }
            style={ {
                top:       anchor.top + anchor.height / 2,
                left:      placeLeft ? anchor.left - MODEL_TOOLTIP_GAP : anchor.right + MODEL_TOOLTIP_GAP,
                transform: placeLeft ? 'translate(-100%, -50%)' : 'translateY(-50%)',
            } }
        >
            <span className={ 'ai-model-description-tooltip-title' }>{ model.name }</span>
            <span className={ 'ai-model-description-tooltip-body' }>{ model.description }</span>
        </div>,
        document.body
    );
};

export const sortByAlpha = <T,>( items: T[], getKey: ( item: T ) => string ): T[] =>
    [ ...items ].sort( ( a, b ) => getKey( a ).localeCompare( getKey( b ) ) );

const sortByDateDesc = ( items: Conversation[] ): Conversation[] =>
    [ ...items ].sort( ( a, b ) => new Date( b.date ).getTime() - new Date( a.date ).getTime() );

const formatConversationDate = ( date: string ): string =>
    new Date( date ).toLocaleDateString( undefined, { month: 'short', day: 'numeric' } );

const formatMessageTime = ( date: string ): string =>
    new Date( date ).toLocaleTimeString( undefined, { hour: 'numeric', minute: '2-digit' } );

const base64ToBlob = ( base64: string, contentType: string ): Blob => {
    const binary = atob( base64 );
    const bytes = new Uint8Array( binary.length );
    for( let i = 0; i < binary.length; i++ ) bytes[ i ] = binary.charCodeAt( i );
    return new Blob( [ bytes ], { type: contentType } );
};

/** Downloads an agent-generated attachment directly from its in-memory base64 content. */
const downloadGeneratedDocument = ( attachment: ConversationAttachment ) => {
    if( !attachment.contentBase64 ) return;

    const blob = base64ToBlob( attachment.contentBase64, attachment.contentType || 'application/octet-stream' );
    const href = URL.createObjectURL( blob );
    const anchor = document.createElement( 'a' );
    anchor.href = href;
    anchor.download = attachment.attachmentName;
    document.body.appendChild( anchor );
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL( href );
};

// Seeds the native OS drag with a real File so it can be dropped onto a DocumentDropZone/DocumentVersionMini
// the same way an on-disk file would be, saving the agent's generated document to blob storage.
const handleGeneratedDocumentDragStart = ( event: React.DragEvent<HTMLSpanElement>, attachment: ConversationAttachment ) => {
    if( !attachment.contentBase64 ) return;

    const blob = base64ToBlob( attachment.contentBase64, attachment.contentType || 'application/octet-stream' );
    const file = new File( [ blob ], attachment.attachmentName, { type: blob.type } );
    event.dataTransfer.items.add( file );
    event.dataTransfer.effectAllowed = 'copy';
};

/**
 * Right-hand panel of the Consultant Dash, mirroring ConsultantDashSidebar's
 * collapsible behavior but anchored to the right side of the screen.
 */
const AIWindow: FC<AIWindowProps> = ( {
    clientId,
    consultantId,
    mode,
    toggleCollapsed,
    toggleExtended,
} ) => {

    const isCollapsed = mode === 'collapsed';
    const isExtended  = mode === 'extended';

    const modalActions  = useSystemModals(),
          toastActions  = useSystemToasts(),
          { TableSelect } = useCentralCalls(),
          { sendTngAgentPrompt, listAIConversations, listSharedAIConversations, listAIConversationMessages, copyAIConversation, updateAIConversationSharing } = useResearchApi(),
          { currentUser } = useCurrentUser();

    const accountId = currentUser?.id;

    const [ hoveredModel, setHoveredModel ]                       = useState<{ model: AIModel; anchor: DOMRect } | null>( null ),
          [ agents, setAgents ]                                   = useState<Agent[]>( [] ),
          [ models, setModels ]                                   = useState<AIModel[]>( [] ),
          [ selectedAgentId, setSelectedAgentId ]                 = useState( '' ),
          [ selectedModelId, setSelectedModelId ]                 = useState( '' ),
          [ conversations, setConversations ]                     = useState<Conversation[]>( [] ),
          [ sharedConversations, setSharedConversations ]         = useState<Conversation[]>( [] ),
          [ selectedConversationId, setSelectedConversationId ]   = useState( '' ),
          [ isConversationListOpen, setIsConversationListOpen ]   = useState( false ),
          [ promptText, setPromptText ]                           = useState( '' ),
          [ contextDocuments, setContextDocuments ]               = useState<AIContextDocument[]>( [] ),
          [ isContextDragOver, setIsContextDragOver ]             = useState( false ),
          [ messagesByConversation, setMessagesByConversation ]   = useState<Record<string, ConversationMessage[]>>( {} ),
          [ isSendingPrompt, setIsSendingPrompt ]                 = useState( false ),
          [ shareDraft, setShareDraft ]                           = useState<ShareDraft | null>( null ),
          [ isSavingShare, setIsSavingShare ]                     = useState( false ),
          [ isCopyingConversation, setIsCopyingConversation ]     = useState( false );

    // Conversation to select once the list for a newly selected agent loads (e.g. a copy made with another agent).
    const pendingConversationIdRef = useRef<string | null>( null );

    const isShareMode = shareDraft !== null;

    const selectedConversationMessages = useMemo(
        () => messagesByConversation[ selectedConversationId || NEW_CONVERSATION_KEY ] ?? [],
        [ messagesByConversation, selectedConversationId ]
    );

    // Unsaved share selections belong to one conversation, so leaving it discards them.
    useEffect( () => {
        setShareDraft( null );
    }, [ selectedConversationId ] );

    // Conversations are scoped to one agent, so switching agents starts a fresh (unsaved) conversation.
    useEffect( () => {
        setSelectedConversationId( '' );
    }, [ selectedAgentId ] );

    // Conversation list for the selected agent + current account.
    useEffect( () => {
        if( !accountId || !selectedAgentId ) {
            setConversations( [] );
            return;
        }

        let cancelled = false;

        ( async () => {
            try {
                const rows = await listAIConversations( accountId, selectedAgentId );
                if( cancelled ) return;

                const mapped = sortByDateDesc( rows.map( row => ( {
                    conversationId: row.id,
                    name:           row.name,
                    date:           row.updatedUtc,
                    isShared:       row.isShared,
                    agentId:        selectedAgentId,
                } ) ) );

                setConversations( mapped );
                const pendingId = pendingConversationIdRef.current;
                pendingConversationIdRef.current = null;
                if( pendingId && mapped.some( c => c.conversationId === pendingId ) ) setSelectedConversationId( pendingId );
                else if( mapped.length > 0 ) setSelectedConversationId( mapped[ 0 ].conversationId );
            } catch( e ) {
                console.error( 'Failed to load AI conversations', e );
                if( !cancelled ) setConversations( [] );
            }
        } )();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ accountId, selectedAgentId ] );

    // Shared conversations from anyone, for any of the selected client's agents.
    const loadSharedConversations = useCallback( async () => {
        if( !clientId ) return [];

        const rows = await listSharedAIConversations( clientId );
        return sortByDateDesc( rows.map( row => ( {
            conversationId: row.id,
            name:           row.name,
            date:           row.updatedUtc,
            isShared:       true,
            agentId:        row.agentId,
            agentName:      row.agentName,
            ownerName:      row.ownerName,
        } ) ) );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ clientId ] );

    useEffect( () => {
        let cancelled = false;

        ( async () => {
            try {
                const rows = await loadSharedConversations();
                if( !cancelled ) setSharedConversations( rows );
            } catch( e ) {
                console.error( 'Failed to load shared AI conversations', e );
                if( !cancelled ) setSharedConversations( [] );
            }
        } )();

        return () => { cancelled = true; };
    }, [ loadSharedConversations ] );

    // Message history for the selected conversation, fetched once and cached.
    useEffect( () => {
        if( !selectedConversationId || !accountId || messagesByConversation[ selectedConversationId ] ) return;

        // A shared conversation may have been held with an agent other than the selected one.
        const conversationAgentId = [ ...conversations, ...sharedConversations ]
            .find( c => c.conversationId === selectedConversationId )?.agentId ?? selectedAgentId;

        let cancelled = false;

        ( async () => {
            try {
                const rows = await listAIConversationMessages( selectedConversationId, accountId );
                if( cancelled ) return;

                const expanded: ConversationMessage[] = [];
                rows.forEach( row => {
                    expanded.push( {
                        type:        'prompt',
                        content:     row.userPrompt,
                        date:        row.createdUtc,
                        userId:      accountId ?? '',
                        attachments: [],
                        messageId:   row.id,
                        isShared:    row.isShared,
                    } );
                    expanded.push( {
                        type:        'response',
                        content:     row.isError ? ( row.errorMessage || 'Something went wrong.' ) : row.aiResponse,
                        date:        row.createdUtc,
                        userId:      conversationAgentId,
                        attachments: [],
                        messageId:   row.id,
                        isShared:    row.isShared,
                    } );
                } );

                setMessagesByConversation( prev => ( { ...prev, [ selectedConversationId ]: expanded } ) );
            } catch( e ) {
                console.error( 'Failed to load AI conversation messages', e );
            }
        } )();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ selectedConversationId ] );


    useEffect( () => {
        let cancelled = false;

        ( async () => {
            try {
                const rows = await TableSelect( 'AIModels', { active: true } ) as AIModel[];
                if( cancelled ) return;
                setModels( sortByAlpha( Array.isArray( rows ) ? rows : [], m => m.name ) );
            } catch( e ) {
                console.error( 'Failed to load AI models', e );
            }
        } )();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    // Agents for the selected client + consultant. A missing id is sent as null, which matches agents with no client/consultant.
    const loadAgents = useCallback( async () => {
        const rows = await TableSelect( 'AIAgents', {
            clientId:     clientId ?? null,
            consultantId: consultantId ?? null,
            active:       true,
        } ) as Agent[];
        return sortByAlpha( Array.isArray( rows ) ? rows : [], a => a.name );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ clientId, consultantId ] );

    const reloadAgents = useCallback( async () => {
        try {
            setAgents( await loadAgents() );
        } catch( e ) {
            console.error( 'Failed to load AI agents', e );
            setAgents( [] );
        }
    }, [ loadAgents ] );

    useEffect( () => {
        let cancelled = false;

        ( async () => {
            try {
                console.log("LAODING AGENTS>>>.")
                console.log(consultantId);
                console.log(clientId);
                const rows = await loadAgents();
                if( !cancelled ) setAgents( rows );
            } catch( e ) {
                console.error( 'Failed to load AI agents', e );
                if( !cancelled ) setAgents( [] );
            }
        } )();

        return () => { cancelled = true; };
    }, [ loadAgents ] );

    // Keep the selected agent valid as the list changes, and default the model to the agent's default model.
    useEffect( () => {
        if( agents.some( agent => agent.id === selectedAgentId ) ) return;
        handleSelectAgent( agents[ 0 ]?.id ?? '' );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ agents ] );

    useEffect( () => {
        if( models.some( model => model.id === selectedModelId ) ) return;
        const agentDefaultId = agents.find( agent => agent.id === selectedAgentId )?.defaultAIModelId;
        setSelectedModelId( models.some( model => model.id === agentDefaultId ) ? agentDefaultId! : ( models[ 0 ]?.id ?? '' ) );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ models ] );

    const handleSelectAgent = ( agentId: string ) => {
        setSelectedAgentId( agentId );
        const defaultModelId = agents.find( agent => agent.id === agentId )?.defaultAIModelId;
        if( defaultModelId && models.some( model => model.id === defaultModelId ) ) setSelectedModelId( defaultModelId );
    };

    const hoverTimeoutRef = useRef<number | undefined>( undefined );

    const handleModelMouseEnter = ( model: AIModel, event: React.MouseEvent<HTMLElement> ) => {
        window.clearTimeout( hoverTimeoutRef.current );
        if( !model.description?.trim() ) return;
        const anchor = event.currentTarget.getBoundingClientRect();
        hoverTimeoutRef.current = window.setTimeout( () => setHoveredModel( { model, anchor } ), 250 );
    };

    const clearModelHover = () => {
        window.clearTimeout( hoverTimeoutRef.current );
        setHoveredModel( null );
    };

    useEffect( () => () => window.clearTimeout( hoverTimeoutRef.current ), [] );

    const selectedAgent        = agents.find( agent => agent.id === selectedAgentId );
    const selectedModel        = models.find( model => model.id === selectedModelId );
    const selectedConversation = conversations.find( c => c.conversationId === selectedConversationId )
        ?? sharedConversations.find( c => c.conversationId === selectedConversationId );

    // Only your own conversations with the selected agent can be continued or re-shared; anything opened from
    // the shared list (someone else's, or yours with another agent) is view-only.
    const isReadOnlyConversation = !!selectedConversation
        && !conversations.some( c => c.conversationId === selectedConversationId );

    // Continue a copy with the agent it was held with when you have access to it; otherwise with the selected agent.
    const copyTargetAgentId = selectedConversation && agents.some( agent => agent.id === selectedConversation.agentId )
        ? selectedConversation.agentId
        : selectedAgentId;

    const handleCopyConversation = async () => {
        if( !selectedConversation || !accountId || !copyTargetAgentId || isCopyingConversation ) return;

        setIsCopyingConversation( true );

        try {
            const copy = await copyAIConversation( selectedConversation.conversationId, accountId, copyTargetAgentId );
            const copiedConversation: Conversation = {
                conversationId: copy.id,
                name:           copy.name,
                date:           copy.updatedUtc,
                isShared:       false,
                agentId:        copy.agentId,
            };

            if( copy.agentId === selectedAgentId ){
                setConversations( prev => [ copiedConversation, ...prev ] );
                setSelectedConversationId( copy.id );
            } else {
                // Switching agents reloads that agent's list, which then selects the copy.
                pendingConversationIdRef.current = copy.id;
                handleSelectAgent( copy.agentId );
            }
            setIsConversationListOpen( false );
        } catch( e ) {
            console.error( 'Failed to copy AI conversation', e );
            toastActions.error( 'Conversation not copied', 'The conversation could not be copied. Please try again.' );
        } finally {
            setIsCopyingConversation( false );
        }
    };

    const agentNameById = ( agentId: string ): string =>
        agents.find( agent => agent.id === agentId )?.name
        ?? sharedConversations.find( c => c.agentId === agentId )?.agentName
        ?? 'Agent';

    const handleOpenEditAgent = () => {
        modalActions.show(
            <AIAgentManagement
                clientId={ clientId }
                initialAgentId={ selectedAgentId }
                onClose={ () => modalActions.toggle( false ) }
                onSaved={ reloadAgents }
            />,
            { size: 'large', centered: true }
        );
    };

    const handleOpenNewAgent = () => {
        modalActions.show(
            <AIAgentManagement
                clientId={ clientId }
                startWithNew
                onClose={ () => modalActions.toggle( false ) }
                onSaved={ reloadAgents }
            />,
            { size: 'large', centered: true }
        );
    };

    const handleSelectConversation =( conversationId: string ) => {
        setSelectedConversationId( conversationId );
        setIsConversationListOpen( false );
    };

    const handleContextDragOver = ( event: React.DragEvent<HTMLDivElement> ) => {
        if( !isAIContextDrag( event.dataTransfer ) ) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        if( !isContextDragOver ) setIsContextDragOver( true );
    };

    const handleContextDragLeave = ( event: React.DragEvent<HTMLDivElement> ) => {
        // Ignore leave events fired when moving between the drop zone's own children.
        if( event.currentTarget.contains( event.relatedTarget as Node ) ) return;
        setIsContextDragOver( false );
    };

    const handleContextDrop = ( event: React.DragEvent<HTMLDivElement> ) => {
        event.preventDefault();
        setIsContextDragOver( false );

        const contextDocument = parseAIContextDrag( event.dataTransfer );
        if( !contextDocument ) return;

        setContextDocuments( prev =>
            prev.some( d => d.documentId === contextDocument.documentId ) ? prev : [ ...prev, contextDocument ]
        );
    };

    const handleRemoveContextDocument = ( documentId: string ) =>
        setContextDocuments( prev => prev.filter( d => d.documentId !== documentId ) );

    const appendMessage = ( conversationKey: string, message: ConversationMessage ) =>
        setMessagesByConversation( prev => ( {
            ...prev,
            [ conversationKey ]: [ ...( prev[ conversationKey ] ?? [] ), message ],
        } ) );

    const handleSendPrompt = async () => {
        const trimmedPrompt = promptText.trim();
        if( !trimmedPrompt || !selectedAgent || !selectedModel || !accountId || isSendingPrompt || isReadOnlyConversation ) return;

        const isNewConversation = !selectedConversationId;
        // Mirrors the server: a new message inherits its conversation's sharing.
        const isMessageShared   = !!selectedConversation?.isShared;
        const conversationKey   = selectedConversationId || NEW_CONVERSATION_KEY;
        const sentDocuments     = contextDocuments;

        appendMessage( conversationKey, {
            type:        'prompt',
            content:     trimmedPrompt,
            date:        new Date().toISOString(),
            userId:      accountId,
            attachments: sentDocuments.map( document => ( {
                attachmentType: 'document',
                attachmentId:   document.documentId,
                attachmentName: formatContextDocumentTitle( document ),
            } ) ),
        } );

        setPromptText( '' );
        setContextDocuments( [] );
        setIsSendingPrompt( true );

        try {
            const result = await sendTngAgentPrompt( {
                agentId:         selectedAgent.id,
                modelId:         selectedModel.id,
                modelIdentifier: selectedModel.modelIdentifier,
                prompt:          trimmedPrompt,
                accountId,
                conversationId:  selectedConversationId || undefined,
                documentIds:     sentDocuments.map( document => document.documentId ),
            } );

            // The prompt was added before the row existed; give it the saved id so the pair can be shared together.
            setMessagesByConversation( prev => {
                const messages = [ ...( prev[ conversationKey ] ?? [] ) ];
                const lastIndex = messages.length - 1;
                if( messages[ lastIndex ]?.type === 'prompt' ) messages[ lastIndex ] = { ...messages[ lastIndex ], messageId: result.messageId, isShared: isMessageShared };
                return { ...prev, [ conversationKey ]: messages };
            } );

            appendMessage( conversationKey, {
                type:        'response',
                content:     result.response,
                messageId:   result.messageId,
                isShared:    isMessageShared,
                date:        new Date().toISOString(),
                userId:      selectedAgent.id,
                attachments: result.documents.map( ( document, index ) => ( {
                    attachmentType: 'document',
                    attachmentId:   `generated-${ result.conversationId }-${ index }`,
                    attachmentName: document.name,
                    contentBase64:  document.contentBase64,
                    contentType:    document.contentType,
                } ) ),
            } );

            if( isNewConversation ){
                setMessagesByConversation( prev => {
                    const { [ NEW_CONVERSATION_KEY ]: movedMessages, ...rest } = prev;
                    return { ...rest, [ result.conversationId ]: movedMessages ?? [] };
                } );
                setConversations( prev => [
                    { conversationId: result.conversationId, name: result.conversationName || 'New conversation', date: new Date().toISOString(), isShared: false, agentId: selectedAgent.id },
                    ...prev,
                ] );
                setSelectedConversationId( result.conversationId );
            } else {
                setConversations( prev => sortByDateDesc(
                    prev.map( c => c.conversationId === result.conversationId ? { ...c, date: new Date().toISOString() } : c )
                ) );
            }
        } catch( e ) {
            console.error( 'Failed to send prompt to AI agent', e );
            appendMessage( conversationKey, {
                type:        'response',
                content:     'Sorry, something went wrong sending that message. Please try again.',
                date:        new Date().toISOString(),
                userId:      selectedAgent.id,
                attachments: [],
            } );
        } finally {
            setIsSendingPrompt( false );
        }
    };

    const handleStartNewConversation = () => setSelectedConversationId( '' );

    const shareableMessageIds = useMemo(
        () => Array.from( new Set( selectedConversationMessages.map( m => m.messageId ).filter( ( id ): id is string => !!id ) ) ),
        [ selectedConversationMessages ]
    );

    // A conversation that isn't shared yet starts with itself and every message selected.
    const handleStartSharing = () => {
        if( !selectedConversation ) return;

        setShareDraft( selectedConversation.isShared
            ? {
                isShared:   true,
                messageIds: new Set( selectedConversationMessages.filter( m => m.isShared ).map( m => m.messageId ).filter( ( id ): id is string => !!id ) ),
            }
            : { isShared: true, messageIds: new Set( shareableMessageIds ) }
        );
    };

    const handleCancelSharing = () => setShareDraft( null );

    // Messages can't be shared without their conversation, so toggling the conversation selects or clears all of them.
    const handleToggleConversationShared = ( isShared: boolean ) =>
        setShareDraft( { isShared, messageIds: new Set<string>( isShared ? shareableMessageIds : [] ) } );

    const handleToggleMessageShared = ( messageId: string, isShared: boolean ) =>
        setShareDraft( prev => {
            if( !prev?.isShared ) return prev;
            const messageIds = new Set( prev.messageIds );
            if( isShared ) messageIds.add( messageId ); else messageIds.delete( messageId );
            return { ...prev, messageIds };
        } );

    const handleSaveSharing = async () => {
        if( !shareDraft || !selectedConversationId || !accountId || isSavingShare ) return;

        const sharedMessageIds = shareDraft.isShared ? Array.from( shareDraft.messageIds ) : [];
        setIsSavingShare( true );

        try {
            await updateAIConversationSharing( selectedConversationId, {
                accountId,
                isShared: shareDraft.isShared,
                sharedMessageIds,
            } );

            setConversations( prev => prev.map( c =>
                c.conversationId === selectedConversationId ? { ...c, isShared: shareDraft.isShared } : c
            ) );
            setMessagesByConversation( prev => ( {
                ...prev,
                [ selectedConversationId ]: ( prev[ selectedConversationId ] ?? [] ).map( m =>
                    m.messageId ? { ...m, isShared: sharedMessageIds.includes( m.messageId ) } : m
                ),
            } ) );
            setShareDraft( null );
            loadSharedConversations().then( setSharedConversations ).catch( e => console.error( 'Failed to reload shared AI conversations', e ) );
        } catch( e ) {
            console.error( 'Failed to update AI conversation sharing', e );
            toastActions.error( 'Sharing not saved', 'Your sharing changes could not be saved. Please try again.' );
        } finally {
            setIsSavingShare( false );
        }
    };

    const renderSharedIndicator = ( conversation?: Conversation ) => conversation?.isShared && (
        <span className={ 'ai-conversation-shared-indicator' } title={ 'This is a shared conversation' }>
            <Icon name={ 'users' as SemanticICONS }/>
        </span>
    );

    const renderConversationButton = ( conversation: Conversation, showOwner = false ) => (
        <button
            key={ conversation.conversationId }
            type={ 'button' }
            className={ `ai-conversation-tab ${ selectedConversationId === conversation.conversationId ? 'active' : '' }` }
            onClick={ () => handleSelectConversation( conversation.conversationId ) }
            title={ conversation.name }
        >
            <span className={ 'ai-conversation-tab-heading' }>
                <span className={ 'ai-conversation-tab-name' }>{ conversation.name }</span>
                { renderSharedIndicator( conversation ) }
            </span>
            <span className={ 'ai-conversation-tab-date' }>
                { formatConversationDate( conversation.date ) }
                { showOwner && conversation.ownerName && ` · ${ conversation.ownerName }` }
            </span>
        </button>
    );

    // My conversations with the selected agent, then everything shared for this client.
    const renderConversationList = ( onStartNewConversation: () => void ) => (
        <>
            <Flexbox justify={ FlexJustify.SPACE_BETWEEN } align={ FlexAlign.CENTER } noWrap>
                <span className={ 'ai-conversations-rail-label' }>Conversations</span>
                <Button
                    icon={ 'plus' as SemanticICONS }
                    size={ 'mini' }
                    basic
                    disabled={ !selectedConversationId }
                    onClick={ onStartNewConversation }
                    title={ 'New conversation' }
                />
            </Flexbox>
            { conversations.map( conversation => renderConversationButton( conversation ) ) }

            { clientId && (
                <>
                    <span className={ 'ai-conversations-rail-label ai-conversations-shared-label' }>Shared Conversations</span>
                    { sharedConversations.length > 0
                        ? sharedConversations.map( conversation => renderConversationButton( conversation, true ) )
                        : <span className={ 'ai-conversations-empty' }>No shared conversations yet</span> }
                </>
            ) }
        </>
    );

    const conversationTitle = (
        <span className={ 'ai-conversation-bar-title' }>
            <span className={ 'ai-conversation-bar-name' }>{ selectedConversation?.name ?? 'New conversation' }</span>
            { renderSharedIndicator( selectedConversation ) }
            { selectedConversation && (
                <span className={ 'ai-conversation-bar-date' }>{ formatConversationDate( selectedConversation.date ) }</span>
            ) }
        </span>
    );

    // Share toggle beside the conversation name; in share mode it becomes save (check) / cancel (red x).
    const conversationShareControls = selectedConversation && !isReadOnlyConversation && (
        isShareMode ? (
            <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.END } noWrap className={ 'ai-conversation-share-actions' }>
                <Button
                    icon={ 'check' as SemanticICONS }
                    size={ 'mini' }
                    basic
                    color={ 'green' }
                    loading={ isSavingShare }
                    disabled={ isSavingShare }
                    onClick={ handleSaveSharing }
                    title={ 'Save sharing' }
                />
                <Button
                    icon={ 'close' as SemanticICONS }
                    size={ 'mini' }
                    basic
                    color={ 'red' }
                    disabled={ isSavingShare }
                    onClick={ handleCancelSharing }
                    title={ 'Cancel' }
                />
            </Flexbox>
        ) : (
            <Button
                icon={ 'share alternate' as SemanticICONS }
                size={ 'mini' }
                basic
                disabled={ isSendingPrompt }
                onClick={ handleStartSharing }
                className={ `ai-conversation-share-btn${ selectedConversation.isShared ? ' is-shared' : '' }` }
                title={ selectedConversation.isShared ? 'Shared — edit sharing' : 'Share conversation' }
            />
        )
    );

    const conversationShareCheckbox = isShareMode && (
        <Checkbox
            checked={ shareDraft.isShared }
            disabled={ isSavingShare }
            onChange={ ( _, data ) => handleToggleConversationShared( !!data.checked ) }
            className={ 'ai-conversation-share-checkbox' }
            title={ shareDraft.isShared ? 'Unshare conversation' : 'Share conversation' }
        />
    );

    // A prompt and its response are one saved row, so they're shared (and rendered) together as an exchange.
    const messageExchanges = useMemo( () => {
        const exchanges: ConversationMessage[][] = [];
        selectedConversationMessages.forEach( message => {
            const current = exchanges[ exchanges.length - 1 ];
            if( message.type === 'response' && current && current[ current.length - 1 ].type === 'prompt' ) current.push( message );
            else exchanges.push( [ message ] );
        } );
        return exchanges;
    }, [ selectedConversationMessages ] );

    const renderMessage = ( message: ConversationMessage, key: string ) => (
        <StyledContainer
            key={ key }
            className={ `ai-message ai-message--${ message.type }` }
        >
            <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } noWrap className={ 'ai-message-meta' }>
                { message.type === 'response' && (
                    <span className={ 'ai-message-author' }>
                        <Icon name={ 'magic' as SemanticICONS }/>
                        { agentNameById( message.userId ) }
                    </span>
                ) }
                <span className={ 'ai-message-time' }>{ formatMessageTime( message.date ) }</span>
            </Flexbox>
            <StyledContainer className={ 'ai-message-bubble' }>
                <span className={ 'ai-message-content' }>{ message.content }</span>
                { message.attachments.length > 0 && (
                    <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } className={ 'ai-message-attachments' }>
                        { message.attachments.map( attachment => (
                            <span
                                key={ attachment.attachmentId }
                                className={ `ai-message-attachment${ attachment.contentBase64 ? ' ai-message-attachment-generated' : '' }` }
                                title={ attachment.contentBase64
                                    ? `Click to download, or drag onto a document drop zone to save ${ attachment.attachmentName }`
                                    : attachment.attachmentName }
                                draggable={ !!attachment.contentBase64 }
                                onDragStart={ attachment.contentBase64 ? e => handleGeneratedDocumentDragStart( e, attachment ) : undefined }
                                onClick={ attachment.contentBase64 ? () => downloadGeneratedDocument( attachment ) : undefined }
                            >
                                <Icon name={ ATTACHMENT_ICONS[ attachment.attachmentType ] ?? ( 'file outline' as SemanticICONS ) }/>
                                { attachment.attachmentName }
                                { attachment.contentBase64 && <Icon name={ 'download' as SemanticICONS } className={ 'ai-message-attachment-download-icon' }/> }
                            </span>
                        ) ) }
                    </Flexbox>
                ) }
            </StyledContainer>
        </StyledContainer>
    );

    return (
        <StyledContainer
            className={ 'ai-window-container' }
            bgColor={ CorvalUIColor.WHITE }
            position={ Position.RELATIVE }
        >
            <StyledContainer
                position={ Position.STICKY }
                top={ 0 }
                zIndex={ 200 }
                className={ 'ai-window-header' }
                bgColor={ CorvalUIColor.WHITE }
                borderSize={ [ null, null, Size.SM, null ] }
                borderColor={ CorvalUIColor.DK_SCREEN_25 }
            >
                <Flexbox justify={ FlexJustify.SPACE_BETWEEN } align={ FlexAlign.CENTER } noWrap className={ 'ai-window-toggle-container' }>
                    <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } noWrap className={ 'ai-window-header-lead' }>
                        <Button
                            icon={ ( isCollapsed ? 'angle left' : 'angle right' ) as SemanticICONS }
                            size={ 'mini' }
                            basic
                            onClick={ toggleCollapsed }
                            className={ 'ai-window-toggle-btn-inner' }
                            title={ isCollapsed ? 'Open AI assistant' : 'Collapse AI assistant' }
                        />
                        { !isCollapsed && (
                            <span className={ 'ai-window-title' }>
                                <Icon name={ 'magic' as SemanticICONS } className={ 'ai-window-title-icon' }/>
                                AI Assistant
                            </span>
                        ) }
                    </Flexbox>
                    { !isCollapsed && (
                        <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.END } noWrap className={ 'ai-window-header-actions' }>
                            <Button
                                size={ 'mini' }
                                basic
                                icon={ !isExtended }
                                onClick={ handleOpenNewAgent }
                                className={ 'ai-window-new-agent-btn' }
                                title={ 'New AI agent' }
                            >
                                <Icon name={ 'plus' as SemanticICONS }/>
                                { isExtended && 'New Agent' }
                            </Button>
                            <Button
                                icon={ 'sticky note outline' as SemanticICONS }
                                size={ 'mini' }
                                basic
                                onClick={ () => {} }
                                className={ 'ai-window-instructions-btn' }
                                title={ 'Instructions' }
                            />
                            <Button
                                icon={ ( isExtended ? 'compress' : 'expand' ) as SemanticICONS }
                                size={ 'mini' }
                                basic
                                onClick={ toggleExtended }
                                className={ 'ai-window-extend-btn' }
                                title={ isExtended ? 'Shrink panel' : 'Extend panel' }
                            />
                        </Flexbox>
                    ) }
                </Flexbox>
            </StyledContainer>

            { !isCollapsed && (
                <Flexbox align={ FlexAlign.STRETCH } noWrap className={ 'ai-window-body' }>
                    { isExtended && (
                        <StyledContainer className={ 'ai-conversations-rail' }>
                            { renderConversationList( handleStartNewConversation ) }
                        </StyledContainer>
                    ) }

                    <StyledContainer className={ 'ai-window-content' }>
                        <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } noWrap className={ 'ai-conversation-bar' }>
                            { conversationShareCheckbox }
                            { isExtended ? conversationTitle : (
                                <Popup
                                    on={ 'click' }
                                    position={ 'bottom left' }
                                    basic
                                    open={ isConversationListOpen }
                                    onOpen={ () => setIsConversationListOpen( true ) }
                                    onClose={ () => setIsConversationListOpen( false ) }
                                    className={ 'ai-conversation-popup' }
                                    trigger={
                                        <button
                                            type={ 'button' }
                                            className={ `ai-conversation-bar-trigger${ isConversationListOpen ? ' is-open' : '' }` }
                                            aria-haspopup={ 'listbox' }
                                            aria-expanded={ isConversationListOpen }
                                            title={ 'Switch conversation' }
                                        >
                                            { conversationTitle }
                                            <Icon name={ 'chevron down' } className={ 'ai-conversation-bar-chevron' }/>
                                        </button>
                                    }
                                    content={
                                        <StyledContainer className={ 'ai-conversation-popup-list' }>
                                            { renderConversationList( () => { handleStartNewConversation(); setIsConversationListOpen( false ); } ) }
                                        </StyledContainer>
                                    }
                                />
                            ) }
                            { conversationShareControls }
                        </Flexbox>

                        <StyledContainer className={ 'ai-message-scroll' }>
                            { selectedConversationMessages.length > 0 ? (
                                <StyledContainer className={ 'ai-message-list' }>
                                    { messageExchanges.map( ( exchange, exchangeIndex ) => {
                                        const messageId = exchange[ 0 ].messageId;
                                        const isExchangeShared = !!shareDraft?.isShared && !!messageId && shareDraft.messageIds.has( messageId );

                                        return (
                                            <div
                                                key={ messageId ?? `exchange-${ exchangeIndex }` }
                                                className={ `ai-exchange${ isShareMode ? ' is-share-mode' : '' }${ isShareMode && !isExchangeShared ? ' is-unshared' : '' }` }
                                            >
                                                { isShareMode && (
                                                    <Checkbox
                                                        checked={ isExchangeShared }
                                                        disabled={ !shareDraft.isShared || !messageId || isSavingShare }
                                                        onChange={ ( _, data ) => messageId && handleToggleMessageShared( messageId, !!data.checked ) }
                                                        className={ 'ai-exchange-share-checkbox' }
                                                        title={ !messageId
                                                            ? 'This message was not saved and cannot be shared'
                                                            : shareDraft.isShared ? 'Share this prompt and response' : 'Share the conversation to choose messages' }
                                                    />
                                                ) }
                                                <div className={ 'ai-exchange-messages' }>
                                                    { exchange.map( ( message, messageIndex ) => renderMessage( message, `${ exchangeIndex }-${ messageIndex }` ) ) }
                                                </div>
                                            </div>
                                        );
                                    } ) }
                                </StyledContainer>
                            ) : (
                                <Flexbox justify={ FlexJustify.CENTER } align={ FlexAlign.CENTER } className={ 'ai-window-placeholder' }>
                                    <Icon name={ 'magic' as SemanticICONS }/>
                                    <span>No messages yet</span>
                                </Flexbox>
                            ) }
                        </StyledContainer>

                        { isReadOnlyConversation ? (
                            <div className={ 'ai-copy-conversation' }>
                                <Button
                                    color={ 'purple' }
                                    fluid
                                    loading={ isCopyingConversation }
                                    disabled={ isCopyingConversation || !copyTargetAgentId }
                                    onClick={ handleCopyConversation }
                                    className={ 'ai-copy-conversation-btn' }
                                >
                                    <Icon name={ 'copy outline' as SemanticICONS }/>
                                    Copy conversation
                                </Button>
                                <span className={ 'ai-copy-conversation-hint' }>
                                    { copyTargetAgentId
                                        ? 'Shared conversations are view-only. Copy it to continue it as your own.'
                                        : 'Select an agent to copy this conversation to.' }
                                </span>
                            </div>
                        ) : (
                            <>
                            <div
                                className={ `ai-composer${ isContextDragOver ? ' is-drag-over' : '' }` }
                                onDragOver={ handleContextDragOver }
                                onDragLeave={ handleContextDragLeave }
                                onDrop={ handleContextDrop }
                            >
                                { contextDocuments.length > 0 && (
                                    <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } className={ 'ai-context-documents' }>
                                        { contextDocuments.map( contextDocument => (
                                            <span
                                                key={ contextDocument.documentId }
                                                className={ 'ai-context-document' }
                                                title={ formatContextDocumentTitle( contextDocument ) }
                                            >
                                                <Icon name={ 'file alternate outline' as SemanticICONS }/>
                                                <span className={ 'ai-context-document-name' }>
                                                    { contextDocument.name }
                                                </span>
                                                <Icon
                                                    name={ 'close' as SemanticICONS }
                                                    link
                                                    className={ 'ai-context-document-remove' }
                                                    onClick={ () => handleRemoveContextDocument( contextDocument.documentId ) }
                                                    title={ 'Remove' }
                                                />
                                            </span>
                                        ) ) }
                                    </Flexbox>
                                ) }

                                <textarea
                                    className={ 'ai-prompt-input' }
                                    placeholder={ `Message ${ selectedAgent?.name ?? 'agent' }…` }
                                    rows={ 2 }
                                    value={ promptText }
                                    onChange={ e => setPromptText( e.target.value ) }
                                    onKeyDown={ e => {
                                        if( e.key === 'Enter' && !e.shiftKey ){
                                            e.preventDefault();
                                            handleSendPrompt();
                                        }
                                    } }
                                />

                                <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.SPACE_BETWEEN } noWrap className={ 'ai-composer-toolbar' }>
                                    <Flexbox align={ FlexAlign.CENTER } justify={ FlexJustify.START } noWrap className={ 'ai-composer-pickers' }>
                                        <Dropdown
                                            upward
                                            icon={ 'chevron down' }
                                            className={ 'ai-composer-picker ai-window-agent-select' }
                                            trigger={ <span className={ 'ai-composer-picker-text' }>{ selectedAgent?.name ?? 'Agent' }</span> }
                                        >
                                            <Dropdown.Menu>
                                                <Dropdown.Header content={ 'Agent' }/>
                                                { agents.length === 0 && (
                                                    <Dropdown.Item disabled text={ 'No agents yet' }/>
                                                ) }
                                                { agents.map( agent => (
                                                    <Dropdown.Item
                                                        key={ agent.id }
                                                        text={ agent.name }
                                                        active={ agent.id === selectedAgentId }
                                                        onClick={ () => handleSelectAgent( agent.id ) }
                                                    />
                                                ) ) }
                                                <Dropdown.Divider/>
                                                <Dropdown.Item
                                                    icon={ 'pencil' }
                                                    text={ 'Edit agent…' }
                                                    className={ 'ai-composer-picker-action' }
                                                    onClick={ handleOpenEditAgent }
                                                />
                                            </Dropdown.Menu>
                                        </Dropdown>

                                        <Dropdown
                                            upward
                                            icon={ 'chevron down' }
                                            className={ 'ai-composer-picker ai-window-model-select' }
                                            onClose={ clearModelHover }
                                            trigger={ <span className={ 'ai-composer-picker-text' }>{ selectedModel?.name ?? 'Model' }</span> }
                                        >
                                            <Dropdown.Menu onScroll={ clearModelHover }>
                                                <Dropdown.Header content={ 'Model' }/>
                                                { models.length === 0 && (
                                                    <Dropdown.Item disabled text={ 'No models available' }/>
                                                ) }
                                                { models.map( model => (
                                                    <Dropdown.Item
                                                        key={ model.id }
                                                        active={ model.id === selectedModelId }
                                                        onClick={ () => { clearModelHover(); setSelectedModelId( model.id ); } }
                                                        onMouseEnter={ ( e: React.MouseEvent<HTMLElement> ) => handleModelMouseEnter( model, e ) }
                                                        onMouseLeave={ clearModelHover }
                                                    >
                                                        <Flexbox justify={ FlexJustify.SPACE_BETWEEN } align={ FlexAlign.CENTER } noWrap className={ 'ai-model-option' }>
                                                            <span className={ 'ai-model-option-main' }>
                                                                <span className={ 'ai-model-option-name' }>{ model.name }</span>
                                                                <span className={ 'ai-model-option-company' }>{ model.provider }</span>
                                                            </span>
                                                            <Flexbox align={ FlexAlign.CENTER } noWrap className={ 'ai-model-option-scales' }>
                                                                <CostIndicator cost={ model.cost }/>
                                                                <SpeedIndicator speed={ model.speed }/>
                                                                <ReasoningIndicator reasoning={ model.reasoning }/>
                                                            </Flexbox>
                                                        </Flexbox>
                                                    </Dropdown.Item>
                                                ) ) }
                                            </Dropdown.Menu>
                                        </Dropdown>

                                        { hoveredModel && <ModelDescriptionTooltip model={ hoveredModel.model } anchor={ hoveredModel.anchor }/> }
                                    </Flexbox>

                                    <Button
                                        icon={ 'send' as SemanticICONS }
                                        size={ 'mini' }
                                        color={ 'purple' }
                                        loading={ isSendingPrompt }
                                        disabled={ !promptText.trim() || isSendingPrompt }
                                        onClick={ handleSendPrompt }
                                        className={ 'ai-prompt-send-btn' }
                                        title={ 'Send (Enter)' }
                                    />
                                </Flexbox>
                            </div>

                            <span className={ 'ai-composer-hint' }>
                                <Icon name={ 'paperclip' as SemanticICONS }/>
                                { isContextDragOver ? 'Drop to add as context' : 'Drag documents onto the message box to add context' }
                            </span>
                            </>
                        ) }
                    </StyledContainer>
                </Flexbox>
            ) }
        </StyledContainer>
    );
};

export default AIWindow;
